import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { categories } from '../../data/conditions';
import { AssigneeSelect } from './AssigneeSelect';
import { InlineDateField } from './InlineDateField';
import { InlineTextField } from './InlineTextField';
import { RowActions } from './RowActions';
import { StatusSelect } from './StatusSelect';
import { conditionRowGrid, daysOpen, daysPastEta, etaApplies, formatDate, formatShortDate, getEscalation, isResolved } from '../../utils/conditions';
import type { ConditionPatch } from '../../hooks/useConditions';
import type { Condition } from '../../types/conditions';
interface ConditionRowProps {
  condition: Condition;
  selected: boolean;
  onToggleSelect: () => void;
  onUpdate: (patch: ConditionPatch) => void;
  onComments: () => void;
  onUpload: () => void;
  onLinkDocuments: () => void;
  showCategory?: boolean;
}
export function ConditionRow({
  condition,
  selected,
  onToggleSelect,
  onUpdate,
  onComments,
  onUpload,
  onLinkDocuments,
  showCategory = false
}: ConditionRowProps) {
  const escalation = getEscalation(condition);
  const resolved = isResolved(condition.status);
  const overdue = daysPastEta(condition);
  const name = condition.name;
  return <motion.div layout initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} role="row" className={`${conditionRowGrid} border-b border-slate-100 px-6 py-3.5 transition-colors ${selected ? 'bg-brand/[0.04]' : 'hover:bg-slate-50/70'}`} data-id="element-90" data-mp="2oqi85nl00wj">
      <div role="cell" data-id="element-91" data-mp="gorjvnbo7cd2">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} aria-label={`Select ${name}`} className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" data-id="element-92" data-mp="gn6xikziezqa" />
      </div>

      <div role="cell" className="min-w-0" data-id="element-93" data-mp="1e573iflthbrb">
        <InlineTextField value={condition.name} onSave={v => v && onUpdate({
        name: v
      })} ariaLabel="condition name" textClassName={`text-sm font-medium ${resolved ? 'text-slate-500' : 'text-slate-900'}`} data-id="element-94" data-mp="1e68hosy4qed9" />
        <InlineTextField value={condition.description} onSave={v => onUpdate({
        description: v
      })} ariaLabel={`description for ${name}`} placeholder="Add description…" textClassName="text-sm text-slate-500" data-id="element-95" data-mp="1e0wtn47eswd2" />
        {showCategory && <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500" data-id="element-96" data-mp="10a1j7nh9i77d">
            {categories.find(cat => cat.id === condition.category)?.label}
          </span>}
        {escalation !== 'none' && <p className="mt-0.5 text-xs text-red-600" data-id="element-97" data-mp="ma0hk5t6avqu">
            No ETA · {daysOpen(condition)}d ·{' '}
            {escalation === 'avp' ? 'escalated to AVP of Processing' : 'processor emailed'}
          </p>}
      </div>

      <div role="cell" data-id="element-98" data-mp="2sz15z6iukzfb">
        <StatusSelect value={condition.status} onChange={status => onUpdate({
        status
      })} conditionName={name} data-id="element-99" data-mp="2xydq3jic3se7" />
      </div>

      <div role="cell" className="text-sm tabular-nums text-slate-700" title={formatDate(condition.createdAt)} data-id="element-100" data-mp="31zh8acnuu0il">
        {formatShortDate(condition.createdAt)}
      </div>

      <div role="cell" data-id="element-101" data-mp="2a8t5yxag2p3c">
        <InlineDateField value={condition.requestedAt} onSave={v => onUpdate({
        requestedAt: v
      })} ariaLabel={`Requested date for ${name}`} emptyLabel="Set date" data-id="element-102" data-mp="2a9cmqaocz7fs" />
      </div>

      <div role="cell" data-id="element-103" data-mp="2j8posia5ktqh">
        {resolved ? <InlineDateField value={condition.clearedAt} onSave={v => onUpdate({
        clearedAt: v
      })} ariaLabel={`Cleared date for ${name}`} emptyLabel="Set date" tone="success" data-id="element-104" data-mp="2o07ol30fwo9v" /> : !etaApplies(condition.status) ? <span className="inline-flex items-center gap-1 text-sm text-steel" title={condition.eta ? `ETA was ${formatDate(condition.eta)}` : 'Documents received'} data-id="element-105" data-mp="1vhkaw8nrwbit">
            <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-106" data-mp="3t5o3c8d2qjcc" />
            Met
          </span> : <div data-id="element-107" data-mp="631hnh2xtigt">
            <InlineDateField value={condition.eta} onSave={v => onUpdate({
          eta: v
        })} ariaLabel={overdue > 0 ? `ETA for ${name}, ${overdue} days overdue` : `ETA for ${name}`} emptyLabel="Set ETA" tone={overdue > 0 ? 'danger' : 'default'} data-id="element-108" data-mp="62jbnsqh3pvp" />
            {overdue > 0 && <span className="block text-xs text-red-600" data-id="element-109" data-mp="tq9j1nx8w7dk">
                {overdue} {overdue === 1 ? 'day' : 'days'} overdue
              </span>}
          </div>}
      </div>

      <div role="cell" data-id="element-110" data-mp="2vltfj8askvbg">
        <AssigneeSelect value={condition.assigneeId} onChange={assigneeId => onUpdate({
        assigneeId
      })} conditionName={name} data-id="element-111" data-mp="2riex6azds2uu" />
      </div>

      <div role="cell" data-id="element-112" data-mp="2hgkpnjgm95ab">
        <RowActions condition={condition} onToggleEmail={() => onUpdate({
        includeInEmail: !condition.includeInEmail
      })} onToggleBorrowerVisible={() => onUpdate({
        borrowerVisible: !condition.borrowerVisible
      })} onComments={onComments} onUpload={onUpload} onLinkDocuments={onLinkDocuments} data-id="element-113" data-mp="2lfk2rrci48cu" />
      </div>
    </motion.div>;
}